"use client";

import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Search, Paintbrush, Rocket } from "lucide-react";

const steps = [
  {
    number: "01",
    title: "Align",
    icon: Search,
    description: "Workshops, audit of your current presence, sitemap, and success metrics. You leave with clarity - not a 40-page PDF nobody reads.",
  },
  {
    number: "02",
    title: "Design & Build",
    icon: Paintbrush,
    description: "High-fidelity UI, content support, and production code. Weekly checkpoints so there are no surprises at the finish line.",
  },
  {
    number: "03",
    title: "Ship & Support",
    icon: Rocket,
    description: "Launch, performance checks, analytics, and training. Optional retainers when you want us in your corner after go-live.",
  },
];

export function ProcessSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="process" className="py-32" ref={ref}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          className="max-w-3xl mb-20"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <span className="text-sm text-accent tracking-wide uppercase mb-4 block">
            Process
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight mb-6">
            How engagements run
          </h2>
          <p className="text-xl text-muted-foreground leading-relaxed">
            Three phases, steady checkpoints, and short write-ups when we make a call - so you always know what&apos;s done, what&apos;s next, and why we chose it.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              className="relative"
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.2 }}
            >
              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-12 left-full w-full h-px bg-border -translate-x-1/2 z-0" />
              )}
              
              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-6xl lg:text-7xl font-semibold text-accent/20">
                    {step.number}
                  </span>
                  <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                    <step.icon className="w-6 h-6 text-accent" />
                  </div>
                </div>
                <h3 className="text-2xl font-semibold mb-4">{step.title}</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {step.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Full-Service Banner */}
        <motion.div
          className="mt-24 p-8 md:p-12 bg-muted/50 border border-border"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <span className="text-sm text-accent tracking-wide uppercase mb-4 block">
                Full-Service Studio
              </span>
              <h3 className="text-2xl md:text-3xl font-semibold mb-4">
                One partner from strategy through launch - sites, apps, and the systems behind your ops.
              </h3>
              <p className="text-muted-foreground">
                No handoffs between five vendors. We stay accountable for UX, visuals, implementation, and the custom workflows you depend on.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="text-center p-6 bg-background border border-border">
                <span className="text-3xl font-semibold text-accent">100%</span>
                <p className="text-sm text-muted-foreground mt-2">Owned by you</p>
              </div>
              <div className="text-center p-6 bg-background border border-border">
                <span className="text-3xl font-semibold">0</span>
                <p className="text-sm text-muted-foreground mt-2">Vendor handoffs</p>
              </div>
              <div className="text-center p-6 bg-background border border-border">
                <span className="text-3xl font-semibold">Weekly</span>
                <p className="text-sm text-muted-foreground mt-2">Check-ins</p>
              </div>
              <div className="text-center p-6 bg-background border border-border">
                <span className="text-3xl font-semibold text-accent">1</span>
                <p className="text-sm text-muted-foreground mt-2">Point of contact</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
